import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, X } from "lucide-react";
import { useCorporate } from "../CorporateContext";
import { updateCorporate } from "../../../../Services/adminCorporates";
import amplitudeService from "../../../../Services/amplitude";
import {
  updateCorporateSchema,
  DOMAIN_REGEX,
  toCorporatePayload,
} from "../schemas";
import { Skeleton } from "../../../../ui/skeleton";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Switch } from "../../../../ui/switch";

const Row = ({ label, value }) => (
  <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-1 md:gap-4 py-2 border-b last:border-b-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm">{value || <span className="text-muted-foreground">—</span>}</span>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return null;
  }
};

const Field = ({ label, htmlFor, error, hint, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-1 md:gap-4 py-2 border-b last:border-b-0 items-start">
    <Label htmlFor={htmlFor} className="text-sm text-muted-foreground pt-2">
      {label}
    </Label>
    <div className="grid gap-1">
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  </div>
);

const corporateToFormValues = (corp) => ({
  name: corp?.name || "",
  display_name: corp?.display_name || "",
  industry: corp?.industry || "",
  timezone: corp?.timezone || "",
  support_email: corp?.support_email || "",
  support_phone: corp?.support_phone || "",
  account_owner_email: corp?.account_owner_email || "",
  contract_seat_limit:
    corp?.contract_seat_limit == null ? "" : String(corp.contract_seat_limit),
  contract_start_date: corp?.contract_start_date || "",
  contract_end_date: corp?.contract_end_date || "",
  allow_personal_email_login: corp?.allow_personal_email_login !== false,
  default_subscription_plan_id: corp?.default_subscription_plan_id || "",
  primary_color: corp?.primary_color || "",
  secondary_color: corp?.secondary_color || "",
  verification_email_domains: (corp?.verification_email_domains || []).map(
    (value) => ({ value })
  ),
});

const ReadView = ({ corporate, onEdit }) => {
  const seatLine =
    corporate.contract_seat_limit == null || corporate.contract_seat_limit === 0
      ? `${corporate.seats_used ?? 0} used / unlimited`
      : `${corporate.seats_used ?? 0} / ${corporate.contract_seat_limit}`;

  return (
    <div>
      <div className="flex items-center justify-end mb-2">
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
        </Button>
      </div>
      <Row label="Name" value={corporate.name} />
      <Row label="Display name" value={corporate.display_name} />
      <Row label="Slug" value={corporate.slug} />
      <Row label="Status" value={corporate.status} />
      <Row label="Industry" value={corporate.industry} />
      <Row label="Timezone" value={corporate.timezone} />
      <Row
        label="Verification domains"
        value={
          corporate.verification_email_domains?.length
            ? corporate.verification_email_domains.join(", ")
            : null
        }
      />
      <Row label="Seats" value={seatLine} />
      <Row label="Contract start" value={formatDate(corporate.contract_start_date)} />
      <Row label="Contract end" value={formatDate(corporate.contract_end_date)} />
      <Row label="Support email" value={corporate.support_email} />
      <Row label="Support phone" value={corporate.support_phone} />
      <Row label="Account owner" value={corporate.account_owner_email} />
      <Row
        label="Personal-email login"
        value={
          corporate.allow_personal_email_login === false ? "Disabled" : "Enabled"
        }
      />
      <Row
        label="Default plan template"
        value={corporate.default_subscription_plan_id}
      />
      <Row label="Created" value={formatDate(corporate.created_at)} />
      <Row label="Last updated" value={formatDate(corporate.updated_at)} />
    </div>
  );
};

const EditView = ({ corporate, onCancel, onSaved }) => {
  const { id } = useParams();
  const [domainInput, setDomainInput] = useState("");
  const [domainTypingError, setDomainTypingError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [savedNotice, setSavedNotice] = useState(null);

  const initialValues = useMemo(
    () => corporateToFormValues(corporate),
    [corporate]
  );

  const form = useForm({
    resolver: zodResolver(updateCorporateSchema),
    defaultValues: initialValues,
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "verification_email_domains",
  });

  const handleAddDomain = () => {
    setDomainTypingError(null);
    const trimmed = domainInput.trim().toLowerCase();
    if (!trimmed) return;
    if (!DOMAIN_REGEX.test(trimmed)) {
      setDomainTypingError("Enter a valid domain (e.g. company.com)");
      return;
    }
    if (fields.some((f) => f.value === trimmed)) {
      setDomainInput("");
      return;
    }
    append({ value: trimmed });
    setDomainInput("");
  };

  const onSubmit = async (values) => {
    setSubmitError(null);
    setSavedNotice(null);

    const patch = toCorporatePayload(values, corporate);

    if (Object.keys(patch).length === 0) {
      setSavedNotice("Nothing to update.");
      return;
    }

    try {
      await updateCorporate(id, patch);
      amplitudeService.track?.("admin_corporate_updated", {
        corporate_id: id,
        changed_fields: Object.keys(patch),
      });
      setSavedNotice("Saved.");
      onSaved?.();
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || "";
      if (status === 422 || status === 400) {
        setError("name", { message });
      }
      setSubmitError(message || "Failed to save changes. Please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Field label="Name" htmlFor="edit_name" error={errors.name?.message}>
        <Input id="edit_name" {...register("name")} />
      </Field>
      <Field
        label="Display name"
        htmlFor="edit_display_name"
        error={errors.display_name?.message}
        hint="Defaults to Name if left blank."
      >
        <Input id="edit_display_name" {...register("display_name")} />
      </Field>
      <Field label="Slug">
        <Input value={corporate.slug || ""} disabled />
        <p className="text-xs text-muted-foreground">
          Slugs are immutable after creation.
        </p>
      </Field>
      <Field label="Status">
        <Input value={corporate.status || ""} disabled />
      </Field>
      <Field
        label="Industry"
        htmlFor="edit_industry"
        error={errors.industry?.message}
      >
        <Input id="edit_industry" {...register("industry")} />
      </Field>
      <Field
        label="Timezone"
        htmlFor="edit_timezone"
        hint="IANA zone, e.g. America/New_York"
        error={errors.timezone?.message}
      >
        <Input id="edit_timezone" {...register("timezone")} />
      </Field>
      <Field
        label="Verification domains"
        htmlFor="edit_domain_input"
        error={domainTypingError}
        hint="Press Enter or click Add to include each domain."
      >
        <div className="flex gap-2">
          <Input
            id="edit_domain_input"
            placeholder="company.com"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddDomain();
              }
            }}
          />
          <Button type="button" variant="outline" onClick={handleAddDomain}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        {fields.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {fields.map((field, idx) => (
              <span
                key={field.id}
                className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs"
              >
                {field.value}
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </Field>
      <Field
        label="Seat limit"
        htmlFor="edit_contract_seat_limit"
        hint="0 means no cap."
        error={errors.contract_seat_limit?.message}
      >
        <Input
          id="edit_contract_seat_limit"
          type="number"
          min={0}
          {...register("contract_seat_limit")}
        />
      </Field>
      <Field
        label="Contract start"
        htmlFor="edit_contract_start_date"
        error={errors.contract_start_date?.message}
      >
        <Input
          id="edit_contract_start_date"
          type="date"
          {...register("contract_start_date")}
        />
      </Field>
      <Field
        label="Contract end"
        htmlFor="edit_contract_end_date"
        error={errors.contract_end_date?.message}
      >
        <Input
          id="edit_contract_end_date"
          type="date"
          {...register("contract_end_date")}
        />
      </Field>
      <Field
        label="Support email"
        htmlFor="edit_support_email"
        error={errors.support_email?.message}
      >
        <Input
          id="edit_support_email"
          type="email"
          {...register("support_email")}
        />
      </Field>
      <Field
        label="Support phone"
        htmlFor="edit_support_phone"
        error={errors.support_phone?.message}
      >
        <Input id="edit_support_phone" {...register("support_phone")} />
      </Field>
      <Field
        label="Account owner"
        htmlFor="edit_account_owner_email"
        error={errors.account_owner_email?.message}
      >
        <Input
          id="edit_account_owner_email"
          type="email"
          {...register("account_owner_email")}
        />
      </Field>
      <Field label="Personal-email login">
        <div className="flex items-center gap-2 pt-2">
          <Switch
            checked={watch("allow_personal_email_login")}
            onCheckedChange={(checked) =>
              setValue("allow_personal_email_login", checked)
            }
          />
          <span className="text-sm">
            {watch("allow_personal_email_login") ? "Enabled" : "Disabled"}
          </span>
        </div>
      </Field>
      <Field
        label="Default plan template"
        htmlFor="edit_default_plan"
        hint="Optional — pulled from subscription-lambda."
        error={errors.default_subscription_plan_id?.message}
      >
        <Input
          id="edit_default_plan"
          {...register("default_subscription_plan_id")}
        />
      </Field>

      {submitError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive mt-4">
          {submitError}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-4">
        {savedNotice && (
          <span className="text-sm text-emerald-600 mr-auto">{savedNotice}</span>
        )}
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
};

const Overview = () => {
  const { corporate, loading, refetch } = useCorporate();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setIsEditing(false);
  }, [corporate?.id]);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    );
  }

  if (!corporate) return null;

  if (isEditing) {
    return (
      <EditView
        corporate={corporate}
        onCancel={() => setIsEditing(false)}
        onSaved={async () => {
          await refetch();
          setIsEditing(false);
        }}
      />
    );
  }

  return <ReadView corporate={corporate} onEdit={() => setIsEditing(true)} />;
};

export default Overview;
